import { pillarArticles } from '@/mocks/articles';
import { SITE_URL, SITE_NAME, SITE_IMAGE, LAST_MODIFIED } from './site';

export interface SeoData {
  title: string;
  description: string;
  keywords: string;
  canonical: string;
  ogType: 'website' | 'article';
  ogImage?: string;
  robots?: string;
  lastModified: string;
  jsonLd: object[];
}

const absolute = (path: string): string => `${SITE_URL}${path}`;

const organizationSchema = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: SITE_NAME,
  url: SITE_URL,
  logo: SITE_IMAGE,
};

const websiteSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: SITE_NAME,
  url: SITE_URL,
  inLanguage: ['pt-BR', 'de', 'en'],
  publisher: {
    '@type': 'Organization',
    name: SITE_NAME,
    url: SITE_URL,
    logo: SITE_IMAGE,
  },
};

function breadcrumbSchema(items: { name: string; path: string }[]) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: absolute(item.path),
    })),
  };
}

function articleSchema(data: {
  headline: string;
  description: string;
  path: string;
  dateModified: string;
}) {
  return {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: data.headline,
    description: data.description,
    image: SITE_IMAGE,
    author: { '@type': 'Organization', name: SITE_NAME },
    publisher: {
      '@type': 'Organization',
      name: SITE_NAME,
      url: SITE_URL,
      logo: SITE_IMAGE,
    },
    datePublished: data.dateModified,
    dateModified: data.dateModified,
    inLanguage: 'en',
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': absolute(data.path),
    },
  };
}

function webPageSchema(data: { name: string; description: string; path: string }) {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: data.name,
    description: data.description,
    url: absolute(data.path),
    inLanguage: 'en',
    isPartOf: { '@type': 'WebSite', name: SITE_NAME, url: SITE_URL },
    publisher: {
      '@type': 'Organization',
      name: SITE_NAME,
      url: SITE_URL,
      logo: SITE_IMAGE,
    },
  };
}

const homeSeo: SeoData = {
  title: 'CSSBuy Spreadsheet: Guias, Categorias e Recursos | CSSBuyBR',
  description:
    'Explore o CSSBuy Spreadsheet com guias de compra, categorias de produtos, dicas de envio e informações de QC. Recurso independente para pesquisa de produtos.',
  keywords:
    'cssbuy spreadsheet, cssbuy guia, cssbuy categorias, cssbuy envio, cssbuy review',
  canonical: absolute('/'),
  ogType: 'website',
  ogImage: SITE_IMAGE,
  lastModified: LAST_MODIFIED,
  jsonLd: [organizationSchema, websiteSchema],
};

const spreadsheetSeo: SeoData = {
  title: 'CSSBuy Spreadsheet: Complete Guide 2026 | CSSBuyBR',
  description:
    'Complete guide to the CSSBuy Spreadsheet 2026. Learn about product categories, links, QC, shipping, safety tips and how to use the spreadsheet.',
  keywords:
    'cssbuy spreadsheet, cssbuy spreadsheet guide, cssbuy spreadsheet 2026, cssbuy spreadsheet categories, cssbuy spreadsheet links, cssbuy spreadsheet products',
  canonical: absolute('/cssbuy-spreadsheet/'),
  ogType: 'article',
  ogImage: SITE_IMAGE,
  lastModified: LAST_MODIFIED,
  jsonLd: [
    articleSchema({
      headline: 'CSSBuy Spreadsheet: Complete Guide 2026',
      description:
        'The complete guide to the CSSBuy Spreadsheet including categories, product links, QC, shipping and safety information.',
      path: '/cssbuy-spreadsheet/',
      dateModified: LAST_MODIFIED,
    }),
    breadcrumbSchema([
      { name: 'Home', path: '/' },
      { name: 'CSSBuy Spreadsheet', path: '/cssbuy-spreadsheet/' },
    ]),
  ],
};

const utilityPages: Record<
  string,
  { title: string; description: string; keywords: string; name: string }
> = {
  '/about/': {
    title: 'About | CSSBuyBR',
    description:
      'About CSSBuyBR, an independent resource for CSSBuy Spreadsheet research, product guides and buying information.',
    keywords: 'about cssbuybr, cssbuy resource, cssbuy spreadsheet about, cssbuy guide',
    name: 'About',
  },
  '/disclaimer/': {
    title: 'Disclaimer | CSSBuyBR',
    description:
      'Disclaimer for CSSBuyBR. This is an independent resource not officially affiliated with CSSBuy.',
    keywords: 'cssbuy disclaimer, cssbuybr disclaimer, cssbuy independent resource',
    name: 'Disclaimer',
  },
  '/privacy/': {
    title: 'Privacy Policy | CSSBuyBR',
    description: 'Privacy policy for the CSSBuyBR website.',
    keywords: 'cssbuy privacy, cssbuybr privacy policy',
    name: 'Privacy Policy',
  },
  '/terms/': {
    title: 'Terms of Service | CSSBuyBR',
    description: 'Terms of service for the CSSBuyBR website.',
    keywords: 'cssbuy terms, cssbuybr terms of service',
    name: 'Terms of Service',
  },
  '/contact/': {
    title: 'Contact | CSSBuyBR',
    description:
      'Contact CSSBuyBR for questions about CSSBuy Spreadsheet resources and product guides.',
    keywords: 'cssbuy contact, cssbuybr contact, cssbuy spreadsheet help',
    name: 'Contact',
  },
  '/editorial-policy/': {
    title: 'Editorial Policy | CSSBuyBR',
    description:
      'Read the CSSBuyBR editorial policy, including standards for accuracy, independence, sourcing, updates and corrections.',
    keywords: 'cssbuy editorial policy, cssbuybr editorial, cssbuy content standards',
    name: 'Editorial Policy',
  },
  '/methodology/': {
    title: 'Methodology | CSSBuyBR',
    description:
      'Learn about the CSSBuyBR research methodology, how we gather information and keep content current.',
    keywords: 'cssbuy methodology, cssbuybr research, cssbuy content methodology',
    name: 'Methodology',
  },
};

export function resolveSeo(pathname: string): SeoData {
  if (pathname === '/' || pathname === '') {
    return homeSeo;
  }

  if (pathname === '/cssbuy-spreadsheet/') {
    return spreadsheetSeo;
  }

  const article = pillarArticles.find((a) => `/${a.slug}/` === pathname);
  if (article) {
    return {
      title: article.metaTitle,
      description: article.metaDescription,
      keywords: article.keywords,
      canonical: absolute(`/${article.slug}/`),
      ogType: 'article',
      ogImage: SITE_IMAGE,
      lastModified: article.updated,
      jsonLd: [
        articleSchema({
          headline: article.title,
          description: article.metaDescription,
          path: `/${article.slug}/`,
          dateModified: article.updated,
        }),
        breadcrumbSchema([
          { name: 'Home', path: '/' },
          { name: article.category, path: `/${article.slug}/` },
        ]),
      ],
    };
  }

  const utility = utilityPages[pathname];
  if (utility) {
    return {
      title: utility.title,
      description: utility.description,
      keywords: utility.keywords,
      canonical: absolute(pathname),
      ogType: 'website',
      ogImage: SITE_IMAGE,
      lastModified: LAST_MODIFIED,
      jsonLd: [
        webPageSchema({
          name: utility.name,
          description: utility.description,
          path: pathname,
        }),
        breadcrumbSchema([
          { name: 'Home', path: '/' },
          { name: utility.name, path: pathname },
        ]),
      ],
    };
  }

  return {
    title: 'Page Not Found | CSSBuyBR',
    description: 'The page you are looking for could not be found.',
    keywords: '',
    canonical: '',
    ogType: 'website',
    robots: 'noindex, nofollow',
    lastModified: LAST_MODIFIED,
    jsonLd: [],
  };
}